<template>
  <article data-component-name="ArticlePreviewPicture">
    <div class="left">
      <h2 v-html="article.data.title" />

      <div v-once v-html="article.data.preview" class="markup" />

      <div class="button-wrapper">
        <Buttons.Regular
          v-if="
            (buttonType === ACTION_TEXT || buttonType === ACTION_ICON) &&
            buttonAction === WHITE_PAPER_POPUP
          "
          @click="setFormState('visible', true)"
        >
          {{ preview?.data?.preview_button_text || $t('Download_White_Paper') }}
          <SvgIcon name="download" />
        </Buttons.Regular>

        <Buttons.Link
          v-else-if="buttonType === POST_LINK"
          :to="articleLink"
          target="_blank"
        >
          {{ preview?.data?.preview_button_text || $t('Read_the_Article') }}
          <SvgIcon name="arrow-forward" />
        </Buttons.Link>

        <Buttons.Link
          v-else-if="buttonType === LINK_ICON"
          :to="localePathWithSlash(preview.data.preview_button_link)"
          target="_blank"
        >
          {{ $t('Download_White_Paper') }}
          <SvgIcon name="download" />
        </Buttons.Link>

        <Buttons.Link
          v-else-if="buttonType === LINK_TEXT"
          :to="localePathWithSlash(preview.data.preview_button_link)"
          target="_blank"
        >
          {{ preview?.data?.preview_button_text || $t('Read_the_Article') }}
          <SvgIcon name="arrow-right-up" />
        </Buttons.Link>

        <img
          v-if="preview?.data?.preview_company_logo_url"
          :src="preview?.data?.preview_company_logo_url"
        >
      </div>
    </div>

    <div class="right">
      <template v-if="[LINK_ICON, LINK_TEXT].includes(buttonType)">
        <NuxtLink
          :to="localePathWithSlash(preview.data.preview_button_link)"
          target="_blank"
        >
          <NuxtImg
            :src="preview.data.preview_picture_url"
            width="220"
            height="300"
            alt="cover"
          />
        </NuxtLink>
      </template>

      <template v-else-if="buttonType === POST_LINK">
        <NuxtLink :to="localePathWithSlash(articleLink)" target="_blank">
          <NuxtImg
            :src="preview.data.preview_picture_url"
            width="220"
            height="300"
            alt="cover"
          />
        </NuxtLink>
      </template>

      <template v-else>
        <NuxtImg
          :src="preview.data.preview_picture_url"
          width="220"
          height="300"
          alt="cover"
        />
      </template>
    </div>
  </article>

  <ClientOnly>
    <Teleport to="#overlay">
      <Transition name="scale-fade">
        <Forms.Wrapper
          v-if="formState.visible"
          can-close
          @close="setFormState('visible', false)"
        >
          <template #title>
            <transition name="fade" mode="out-in">
              <strong>
                {{
                  formState.submitted
                    ? $t('Download_Asset')
                    : $t('Provide_your_details_to_get_the_asset')
                }}
              </strong>
            </transition>
          </template>

          <transition name="fade" mode="out-in">
            <Modals.DownloadAsset v-if="formState.submitted" :to="actionURL" />

            <Forms.GetAsset
              v-else
              v-bind="{
                customCampaign: actionCampaign,
                page: actionPage,
              }"
              @loading="setLoading"
              @submitted="setFormState('submitted', true)"
            />
          </transition>
        </Forms.Wrapper>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
// components
import Forms from '~/components/redesign/Forms';
import Modals from '~/components/redesign/Modals';
import Buttons from '~/components/redesign/Buttons';

// types
import {
  PostPreviewButtonAction,
  PostPreviewButtonType,
  type BlogArticle,
} from '~/types';

// composables
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// utils
import get from 'lodash.get';
import { isString } from '~/utils';
import { getArticleLink } from '~/utils/getArticleLink';

// stores
import useUIStore from '~/stores/ui';

const uiStore = useUIStore();
const { setOverlay, setLoading } = uiStore;

const { ACTION_ICON, ACTION_TEXT, LINK_ICON, LINK_TEXT, POST_LINK } =
  PostPreviewButtonType;

const { WHITE_PAPER_POPUP } = PostPreviewButtonAction;

const props = defineProps<{ article: BlogArticle }>();

const preview = computed(() => props.article.data);
const buttonType = computed(
  () => props.article.data?.data?.preview_button_type,
);
const buttonAction = computed(
  () => props.article.data?.data?.preview_button_action,
);
const actionCampaign = computed(
  () => props.article.data.data?.preview_action_campaign,
);
const actionPage = computed(() => {
  const value: null | string | undefined = get(
    props.article,
    'data.data.preview_action_page',
  );

  return isString(value) ? value : undefined;
});
const actionURL = computed(() => props.article.data?.data?.preview_action_url);

const localePathWithSlash = useLocalePathWithSlash();
const articleLink = computed(() =>
  localePathWithSlash(getArticleLink(props.article)),
);

const formState = reactive({ visible: false, submitted: false });
const setFormState = <K extends keyof typeof formState>(
  key: K,
  value: (typeof formState)[K],
) => {
  formState[key] = value;
};

watchEffect(() => {
  setOverlay({ visible: formState.visible });
});
</script>

<style scoped lang="scss">
@use '_/mixins/flex';
@use '_/mixins/text-transform';
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/mixins/media';
@use '_/fn';

article[data-component-name='ArticlePreviewPicture'] {
  @include flex.center-sb;

  gap: 2rem;
  padding: 2.5rem;
  border: 1px solid fn.token('outline-secondary');

  @include media.tablet {
    @include flex.start-sb;

    gap: 1.5rem;
    padding: 1.5rem;
  }

  @include media.mobile {
    padding: 1.5rem 1rem;
  }

  .left {
    @include media.mobile {
      width: 100%;
    }

    h2 {
      @include typo.subtitle-1;

      margin-bottom: 0.5rem;
    }

    .markup {
      @include typo.body-3;

      margin-bottom: 2rem;

      @include text-transform.truncate-to-rows(6);
    }

    .button-wrapper {
      @include flex.stretch-sb;

      align-items: center;
      gap: 2rem;

      @include media.mobile {
        flex-direction: column;
        gap: 1rem;
      }

      [data-component-name='Buttons.Regular'],
      [data-component-name='Buttons.Link'] {
        display: block;
        width: max-content;

        @include media.mobile {
          width: 100%;
        }

        svg {
          fill: fn.token('icon-reversed');
        }
      }

      img {
        max-width: 10rem;
        height: auto;
      }
    }
  }

  .right {
    @include media.mobile {
      display: none;
    }

    img {
      object-fit: contain;
    }
  }
}
</style>
